import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import "./CreateEditSignaturePage.css"; // Crear este archivo CSS

function CreateEditSignaturePage() {
  const [signatureName, setSignatureName] = useState("");
  const [signatureRole, setSignatureRole] = useState("");
  const [signatureImageFile, setSignatureImageFile] = useState(null);
  const [currentSignatureImageUrl, setCurrentSignatureImageUrl] = useState(""); // Para mostrar la firma actual
  const [initialSignatureImageUrl, setInitialSignatureImageUrl] = useState(""); // Para detectar si el logo cambió

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");

  const { id: signatureId } = useParams(); // Obtiene el ID de la URL (si es modo edición)
  const navigate = useNavigate();
  const { token } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // useEffect: Carga datos de la firma si es modo edición
  useEffect(() => {
    const fetchSignatureData = async () => {
      if (!signatureId) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError("");
      try {
        const response = await axios.get(
          `${API_BASE_URL}/signatures/${signatureId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = response.data;
        setSignatureName(data.name);
        setSignatureRole(data.role);
        setCurrentSignatureImageUrl(data.digital_signature_url || "");
        setInitialSignatureImageUrl(data.digital_signature_url || "");
      } catch (err) {
        setError(
          err.response?.data?.error || "Error al cargar los datos de la firma."
        );
        console.error("Error fetching signature data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchSignatureData();
    }
  }, [signatureId, token, API_BASE_URL]);

  const handleFileChange = (event) => {
    setSignatureImageFile(event.target.files[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError("");
    setMessage("");
    setSuccessPopupMessage("");
    setShowSuccessPopup(false);

    if (!signatureName.trim() || !signatureRole.trim()) {
      setError("El nombre y el rol de la firma son requeridos.");
      setIsSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", signatureName.trim());
    formData.append("role", signatureRole.trim());

    // Lógica de imagen de firma para FormData
    if (signatureImageFile) {
      // Se seleccionó un nuevo archivo de imagen
      formData.append("signatureImage", signatureImageFile); // 'signatureImage' es el nombre del campo que Multer espera
    } else if (
      signatureId &&
      initialSignatureImageUrl &&
      !currentSignatureImageUrl
    ) {
      // Si estamos editando, tenía una imagen y ahora se borró (no hay nuevo archivo ni se mantiene el viejo)
      formData.append("clearSignature", "true"); // Indica al backend que borre la imagen existente
    }
    // Si no se toca la imagen, no se añade nada al formData y el backend mantendrá la existente.

    try {
      let response;
      if (signatureId) {
        // Modo edición (PUT)
        response = await axios.put(
          `${API_BASE_URL}/signatures/${signatureId}`,
          formData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setSuccessPopupMessage("Firma actualizada exitosamente.");
      } else {
        // Modo creación (POST)
        response = await axios.post(`${API_BASE_URL}/signatures`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccessPopupMessage("Firma creada exitosamente.");
        // Limpiar formulario solo al crear
        setSignatureName("");
        setSignatureRole("");
        setSignatureImageFile(null);
        setCurrentSignatureImageUrl("");
        setInitialSignatureImageUrl("");
      }
      console.log("Respuesta del backend (guardar firma):", response.data);
      setShowSuccessPopup(true);
    } catch (err) {
      setError(err.response?.data?.error || "Error al guardar la firma.");
      console.error(
        "Error saving signature:",
        err.response?.data || err.message
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClosePopup = () => {
    setShowSuccessPopup(false);
    navigate("/admin/signatures"); // Redirige a la lista de firmas
  };

  if (isLoading) {
    return (
      <div className="create-edit-signature-container">
        <p>Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="create-edit-signature-container">
      <Link to="/admin/signatures" className="back-button">
        <span className="material-icons">arrow_back</span>
      </Link>

      <div className="create-edit-signature-card">
        <h2 className="create-edit-signature-title">
          {signatureId ? "Editar Firma" : "Crear Nueva Firma"}
        </h2>
        <p className="create-edit-signature-description">
          {signatureId
            ? "Modifica la información de la firma existente."
            : "Registra una nueva firma y su imagen digital."}
        </p>

        {message && (
          <p className="create-edit-signature-message success">{message}</p>
        )}
        {error && (
          <p className="create-edit-signature-message error">{error}</p>
        )}

        <form onSubmit={handleSubmit} className="create-edit-signature-form">
          <div className="form-section">
            <h3>Detalles de la Firma</h3>
            <div className="form-group">
              <label htmlFor="signatureName">Nombre del Firmante:</label>
              <input
                type="text"
                id="signatureName"
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                className="signature-input"
                disabled={isSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="signatureRole">Rol del Firmante:</label>
              <input
                type="text"
                id="signatureRole"
                value={signatureRole}
                onChange={(e) => setSignatureRole(e.target.value)}
                className="signature-input"
                disabled={isSaving}
              />
            </div>

            {/* Sección de Imagen de Firma Digital */}
            <div className="form-group">
              <label>Imagen de Firma Digital:</label>
              {signatureId &&
                (initialSignatureImageUrl || currentSignatureImageUrl) &&
                !signatureImageFile && (
                  <div className="current-signature-preview">
                    <img
                      src={`${API_BASE_URL}/uploads/${
                        currentSignatureImageUrl || initialSignatureImageUrl
                      }`}
                      alt={`Firma actual de ${signatureName}`}
                      className="signature-preview-thumbnail"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "https://placehold.co/100x40/cccccc/333333?text=Sin+Firma";
                      }}
                    />
                    <span>Firma actual</span>
                    {currentSignatureImageUrl && (
                      <button
                        type="button"
                        onClick={() =>
                          setCurrentSignatureImageUrl("")
                        } /* Vacía la URL para indicar borrado */
                        className="clear-signature-button"
                        title="Eliminar firma actual"
                      >
                        <span className="material-icons">close</span>
                      </button>
                    )}
                  </div>
                )}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="signature-input-file"
                disabled={isSaving}
              />
              <p className="form-helper-text">
                Se recomienda una imagen PNG con fondo transparente.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="create-edit-signature-submit-button"
          >
            {isSaving
              ? "Guardando..."
              : signatureId
              ? "Actualizar Firma"
              : "Crear Firma"}
          </button>
        </form>
      </div>

      {/* Pop-up de éxito */}
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>¡Éxito!</h3>
            <p>{successPopupMessage}</p>
            <button onClick={handleClosePopup} className="popup-button">
              Cerrar y Volver a Firmas
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateEditSignaturePage;
